/**
 * Favorites Store (Offline-first)
 * Local state management with outbox for background sync
 * With circuit breaker + exponential backoff for resilience
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  favoritesEdgeClient,
  type FavoriteListingCard,
  type SetFavoriteResponse
} from './favoritesEdgeClient';
import { createTraceId, logger } from './logger';
import { invalidateCache, cacheConfig } from '@/lib/config/cacheConfig';

// Re-export type from edge client
export type FavoriteEntityType = 'listing' | 'help_request';

// ============ Types ============

export interface FavoriteOutboxOp {
  entityType: FavoriteEntityType;
  entityId: string;
  desiredState: boolean;
  createdAt: string;
  retryCount?: number;
  lastAttempt?: string;
}

interface FavoritesState {
  // Local state: entityKey -> isFavorited
  local: Map<string, boolean>;
  // Outbox for pending operations (deduplicated by entityKey)
  outbox: Map<string, FavoriteOutboxOp>;
  // Items cache for favorites list
  itemsCache: FavoriteListingCard[];
  cacheUpdatedAt: string | null;
}

// ============ Circuit Breaker State ============

interface CircuitBreakerState {
  failureCount: number;
  lastFailure: number | null;
  isOpen: boolean;
  openedAt: number | null;
}

const CIRCUIT_BREAKER_CONFIG = {
  failureThreshold: 3,       // Open circuit after 3 consecutive failures
  resetTimeout: 60000,       // Try again after 60 seconds
  halfOpenMaxAttempts: 1,    // Only 1 attempt in half-open state
};

const BACKOFF_CONFIG = {
  baseDelay: 1000,           // 1 second initial delay
  maxDelay: 60000,           // Max 60 seconds delay
  maxRetries: 5,             // Max 5 retries per operation
};

let circuitBreaker: CircuitBreakerState = {
  failureCount: 0,
  lastFailure: null,
  isOpen: false,
  openedAt: null,
};

function calculateBackoffDelay(retryCount: number): number {
  const delay = BACKOFF_CONFIG.baseDelay * Math.pow(2, retryCount);
  return Math.min(delay, BACKOFF_CONFIG.maxDelay);
}

function isCircuitOpen(): boolean {
  if (!circuitBreaker.isOpen) return false;
  
  // Check if we should move to half-open
  const now = Date.now();
  if (circuitBreaker.openedAt && now - circuitBreaker.openedAt >= CIRCUIT_BREAKER_CONFIG.resetTimeout) {
    logger.info('[FavoritesStore] Circuit breaker: half-open', {
      context: { feature: 'favorites_store', function_name: 'circuitBreaker' },
    });
    return false; // Allow one attempt
  }
  
  return true;
}

function recordSuccess(): void {
  if (circuitBreaker.failureCount > 0 || circuitBreaker.isOpen) {
    logger.info('[FavoritesStore] Circuit breaker: closed after success', {
      context: { feature: 'favorites_store', function_name: 'circuitBreaker' },
    });
  }
  circuitBreaker = {
    failureCount: 0,
    lastFailure: null,
    isOpen: false,
    openedAt: null,
  };
}

function recordFailure(): void {
  const now = Date.now();
  circuitBreaker.failureCount++;
  circuitBreaker.lastFailure = now;
  
  if (circuitBreaker.failureCount >= CIRCUIT_BREAKER_CONFIG.failureThreshold) {
    circuitBreaker.isOpen = true;
    circuitBreaker.openedAt = now;
    logger.warn('[FavoritesStore] Circuit breaker: OPEN', {
      context: { feature: 'favorites_store', function_name: 'circuitBreaker' },
      data: { failures: circuitBreaker.failureCount, willRetryIn: CIRCUIT_BREAKER_CONFIG.resetTimeout },
      allowList: ['failures', 'willRetryIn'],
    });
  }
}

function shouldSkipDueToBackoff(op: FavoriteOutboxOp): boolean {
  if (!op.lastAttempt || !op.retryCount) return false;
  
  const lastAttemptTime = new Date(op.lastAttempt).getTime();
  const backoffDelay = calculateBackoffDelay(op.retryCount);
  const now = Date.now();
  
  return now - lastAttemptTime < backoffDelay;
}

// ============ Helpers ============

export function favoriteKey(entityType: FavoriteEntityType, entityId: string): string {
  return `${entityType}:${entityId}`;
}

function parseKey(key: string): { entityType: FavoriteEntityType; entityId: string } | null {
  const [entityType, entityId] = key.split(':');
  if (entityType && entityId && ['listing', 'help_request'].includes(entityType)) {
    return { entityType: entityType as FavoriteEntityType, entityId };
  }
  return null;
}

// ============ LocalStorage persistence ============

const STORAGE_KEY = 'drug_favorites_store';

function loadFromStorage(): FavoritesState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return createEmptyState();
    
    const parsed = JSON.parse(stored);
    return {
      local: new Map(Object.entries(parsed.local || {})),
      outbox: new Map(Object.entries(parsed.outbox || {})),
      itemsCache: parsed.itemsCache || [],
      cacheUpdatedAt: parsed.cacheUpdatedAt || null,
    };
  } catch {
    return createEmptyState();
  }
}

function saveToStorage(state: FavoritesState): void {
  try {
    const serializable = {
      local: Object.fromEntries(state.local),
      outbox: Object.fromEntries(state.outbox),
      itemsCache: state.itemsCache,
      cacheUpdatedAt: state.cacheUpdatedAt,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
  } catch (e) {
    logger.warn('[FavoritesStore] Failed to save to localStorage', {
      context: { feature: 'favorites_store', function_name: 'saveToStorage' },
      error: e,
      error_code: 'favorites_store_persist_failed',
      error_type: 'unexpected',
    });
  }
}

function createEmptyState(): FavoritesState {
  return {
    local: new Map(),
    outbox: new Map(),
    itemsCache: [],
    cacheUpdatedAt: null,
  };
}

// ============ Singleton store ============

let globalState: FavoritesState = loadFromStorage();
const listeners = new Set<() => void>();

function notifyListeners(): void {
  listeners.forEach(fn => fn());
}

function getState(): FavoritesState {
  return globalState;
}

function setState(updater: (prev: FavoritesState) => FavoritesState): void {
  globalState = updater(globalState);
  saveToStorage(globalState);
  notifyListeners();
}

// ============ Store API ============

export function isFavorited(entityType: FavoriteEntityType, entityId: string): boolean {
  const key = favoriteKey(entityType, entityId);
  return globalState.local.get(key) ?? false;
}

export function hasPending(entityType: FavoriteEntityType, entityId: string): boolean {
  const key = favoriteKey(entityType, entityId);
  return globalState.outbox.has(key);
}

export function setLocal(entityType: FavoriteEntityType, entityId: string, value: boolean): void {
  setState(prev => {
    const newLocal = new Map(prev.local);
    newLocal.set(favoriteKey(entityType, entityId), value);
    return { ...prev, local: newLocal };
  });
}

export function enqueue(op: Omit<FavoriteOutboxOp, 'createdAt'>): void {
  const key = favoriteKey(op.entityType, op.entityId);
  setState(prev => {
    const newOutbox = new Map(prev.outbox);
    // Deduplication: replace existing op for same entity
    newOutbox.set(key, {
      ...op,
      createdAt: new Date().toISOString(),
      retryCount: 0,
      lastAttempt: undefined,
    });
    return { ...prev, outbox: newOutbox };
  });
}

function updateOutboxOp(entityType: FavoriteEntityType, entityId: string, updates: Partial<FavoriteOutboxOp>): void {
  const key = favoriteKey(entityType, entityId);
  setState(prev => {
    const newOutbox = new Map(prev.outbox);
    const existing = newOutbox.get(key);
    if (existing) {
      newOutbox.set(key, { ...existing, ...updates });
    }
    return { ...prev, outbox: newOutbox };
  });
}

export function removeFromOutbox(entityType: FavoriteEntityType, entityId: string): void {
  const key = favoriteKey(entityType, entityId);
  setState(prev => {
    const newOutbox = new Map(prev.outbox);
    newOutbox.delete(key);
    return { ...prev, outbox: newOutbox };
  });
}

export function updateItemsCache(items: FavoriteListingCard[], append = false): void {
  setState(prev => ({
    ...prev,
    itemsCache: append ? [...prev.itemsCache, ...items] : items,
    cacheUpdatedAt: new Date().toISOString(),
  }));
}

export function removeFromCache(listingId: string): void {
  setState(prev => ({
    ...prev,
    itemsCache: prev.itemsCache.filter(item => item.listing_id !== listingId),
  }));
}

export function reconcileWithServer(results: Record<string, boolean>): void {
  setState(prev => {
    const newLocal = new Map(prev.local);
    for (const [entityId, isFav] of Object.entries(results)) {
      // Only reconcile if not in outbox (pending changes have priority)
      const listingKey = favoriteKey('listing', entityId);
      const helpKey = favoriteKey('help_request', entityId);
      
      if (!prev.outbox.has(listingKey)) {
        newLocal.set(listingKey, isFav);
      }
      if (!prev.outbox.has(helpKey)) {
        newLocal.set(helpKey, isFav);
      }
    }
    return { ...prev, local: newLocal };
  });
}

// ============ Sync functions ============

export async function syncOne(
  entityType: FavoriteEntityType,
  entityId: string
): Promise<SetFavoriteResponse | null> {
  const key = favoriteKey(entityType, entityId);
  const traceId = createTraceId();
  const op = globalState.outbox.get(key);

  if (!op) return null;

  // Circuit breaker check
  if (isCircuitOpen()) {
    logger.info('[FavoritesStore] Sync skipped - circuit open', {
      context: { feature: 'favorites_store', function_name: 'syncOne', request_id: key },
    });
    return null;
  }

  // Backoff check
  if (shouldSkipDueToBackoff(op)) {
    const backoffDelay = calculateBackoffDelay(op.retryCount || 0);
    logger.info('[FavoritesStore] Sync skipped - backoff', {
      context: { feature: 'favorites_store', function_name: 'syncOne', request_id: key },
      data: { retryCount: op.retryCount, backoffMs: backoffDelay },
      allowList: ['retryCount', 'backoffMs'],
    });
    return null;
  }

  // Max retries check
  if ((op.retryCount || 0) >= BACKOFF_CONFIG.maxRetries) {
    logger.warn('[FavoritesStore] Max retries exceeded, removing from outbox', {
      context: { feature: 'favorites_store', function_name: 'syncOne', request_id: key, trace_id: traceId },
      data: { retryCount: op.retryCount },
      allowList: ['retryCount'],
    });
    removeFromOutbox(op.entityType, op.entityId);
    return null;
  }

  try {
    const result = await favoritesEdgeClient.setFavorite(
      op.entityType,
      op.entityId,
      op.desiredState
    );
    
    // Success: reset circuit breaker, remove from outbox and reconcile
    recordSuccess();
    removeFromOutbox(op.entityType, op.entityId);
    setLocal(op.entityType, op.entityId, result.is_favorited);

    logger.info('[FavoritesStore] Sync succeeded', {
      context: { feature: 'favorites_store', function_name: 'syncOne', request_id: key, trace_id: traceId },
      data: { is_favorited: result.is_favorited },
      allowList: ['is_favorited'],
    });

    return result;
  } catch (error) {
    // Update retry info
    updateOutboxOp(op.entityType, op.entityId, {
      retryCount: (op.retryCount || 0) + 1,
      lastAttempt: new Date().toISOString(),
    });
    
    // Record failure for circuit breaker
    recordFailure();
    
    // Only log once per backoff cycle (don't spam)
    if ((op.retryCount || 0) === 0) {
      logger.warn('[FavoritesStore] Sync failed, will retry with backoff', {
        context: { feature: 'favorites_store', function_name: 'syncOne', request_id: key, trace_id: traceId },
        error,
        error_code: 'favorites_store_sync_failed',
        error_type: 'network',
      });
    }
    
    return null;
  }
}

export async function syncAll(): Promise<void> {
  // Circuit breaker check at batch level
  if (isCircuitOpen()) {
    logger.info('[FavoritesStore] syncAll skipped - circuit open', {
      context: { feature: 'favorites_store', function_name: 'syncAll' },
    });
    return;
  }

  const ops = Array.from(globalState.outbox.values());
  
  // Filter ops that are ready to retry (not in backoff)
  const readyOps = ops.filter(op => !shouldSkipDueToBackoff(op));
  
  if (readyOps.length === 0 && ops.length > 0) {
    logger.info('[FavoritesStore] All ops in backoff, skipping sync', {
      context: { feature: 'favorites_store', function_name: 'syncAll' },
      data: { pendingCount: ops.length },
      allowList: ['pendingCount'],
    });
    return;
  }
  
  for (const op of readyOps) {
    // Re-check circuit after each attempt
    if (isCircuitOpen()) {
      logger.info('[FavoritesStore] Circuit opened during sync, stopping', {
        context: { feature: 'favorites_store', function_name: 'syncAll' },
      });
      break;
    }
    await syncOne(op.entityType, op.entityId);
  }
}

// ============ Public circuit breaker status ============

export function getCircuitStatus(): { isOpen: boolean; willRetryAt: number | null } {
  return {
    isOpen: circuitBreaker.isOpen,
    willRetryAt: circuitBreaker.openedAt 
      ? circuitBreaker.openedAt + CIRCUIT_BREAKER_CONFIG.resetTimeout 
      : null,
  };
}

// ============ React Hook ============

export function useFavoritesStore() {
  const [, forceUpdate] = useState(0);
  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const handleUpdate = () => forceUpdate(n => n + 1);
    listeners.add(handleUpdate);

    // Sync pending on mount (if circuit allows)
    if (!isCircuitOpen()) {
      syncAll();
    }

    // Retry sync every 30 seconds (circuit breaker will gate if needed)
    syncIntervalRef.current = setInterval(() => {
      if (globalState.outbox.size > 0) {
        logger.info('[FavoritesStore] Retrying pending ops...', {
          context: { feature: 'favorites_store', function_name: 'syncInterval' },
          data: { pending: globalState.outbox.size, circuitOpen: circuitBreaker.isOpen },
          allowList: ['pending', 'circuitOpen'],
        });
        syncAll();
      }
    }, 30000);

    // Sync on online event (if circuit allows)
    const handleOnline = () => {
      logger.info('[FavoritesStore] Online - syncing...', {
        context: { feature: 'favorites_store', function_name: 'onlineSync' },
      });
      // Reset circuit breaker on network reconnect
      circuitBreaker = {
        failureCount: 0,
        lastFailure: null,
        isOpen: false,
        openedAt: null,
      };
      syncAll();
    };
    window.addEventListener('online', handleOnline);

    return () => {
      listeners.delete(handleUpdate);
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const toggleFavorite = useCallback(async (
    entityType: FavoriteEntityType,
    entityId: string,
    source?: string
  ): Promise<boolean> => {
    const currentState = isFavorited(entityType, entityId);
    const desiredState = !currentState;

    // Optimistic update
    setLocal(entityType, entityId, desiredState);

    // Enqueue for sync (deduplicates)
    enqueue({ entityType, entityId, desiredState });

    // Update cache if removing
    if (!desiredState && entityType === 'listing') {
      removeFromCache(entityId);
    }

    // CRITICAL: Invalidate favorites cache when adding to favorites
    // This ensures the favorites list is refreshed on next visit
    if (desiredState && entityType === 'listing') {
      invalidateCache(cacheConfig.favorites.storageKey);
    }

    // Background sync (only if circuit allows)
    if (!isCircuitOpen()) {
      syncOne(entityType, entityId);
    }

    // Analytics (TODO: implement analytics service)
    logger.info('[FavoritesStore] favorite toggled', {
      context: { feature: 'favorites_store', function_name: 'toggleFavorite', request_id: favoriteKey(entityType, entityId) },
      data: {
        entityType,
        entityId,
        source,
        offline: !navigator.onLine,
        desiredState,
        circuitOpen: circuitBreaker.isOpen,
      },
      allowList: ['entityType', 'entityId', 'source', 'offline', 'desiredState', 'circuitOpen'],
    });

    return desiredState;
  }, []);

  return {
    // State accessors
    isFavorited: useCallback((entityType: FavoriteEntityType, entityId: string) => 
      isFavorited(entityType, entityId), []),
    hasPending: useCallback((entityType: FavoriteEntityType, entityId: string) => 
      hasPending(entityType, entityId), []),
    getItemsCache: useCallback(() => globalState.itemsCache, []),
    getCacheUpdatedAt: useCallback(() => globalState.cacheUpdatedAt, []),
    getOutboxSize: useCallback(() => globalState.outbox.size, []),
    getCircuitStatus: useCallback(() => getCircuitStatus(), []),
    
    // Actions
    toggleFavorite,
    setLocal,
    enqueue,
    syncOne,
    syncAll,
    updateItemsCache,
    removeFromCache,
    reconcileWithServer,
  };
}

export default useFavoritesStore;
